// Local, offline R-T-C-F scorer. No network, no model call. A pure function
// takes the four field strings and returns scores, misses, weakest, and the
// PII result. Rules live here so they can be tuned without touching the UI.

import { config } from "@/config/eventConfig";

// ---- word lists -----------------------------------------------------------
const ROLE_JOB_NOUNS = [
  "officer", "manager", "director", "coordinator", "specialist", "writer",
  "editor", "consultant", "analyst", "teacher", "fundraiser", "grant writer",
  "communications lead",
];
const ROLE_GENERIC = ["assistant", "helper", "ai", "bot", "expert", "professional"];
const ROLE_QUALIFIER_PHRASES = ["in education", "for nonprofits", "who has"];
const ROLE_QUALIFIER_ADJ = ["experienced", "senior", "veteran"];

const TASK_VERBS = [
  "draft", "write", "rewrite", "summarise", "summarize", "list", "outline",
  "compare", "translate", "extract", "plan", "generate", "edit", "turn", "map",
];
const TASK_HEDGES = ["help me", "assist with", "do something about"];
const TASK_STOP = [
  "the", "a", "an", "it", "this", "that", "for", "to", "of", "and", "or",
  "with", "on", "in", "about", "me", "my", "our", "your",
];

const PEOPLE_NOUNS = ["students", "families", "youth", "participants", "clients", "children", "kids"];
const AUDIENCE = ["funder", "funders", "board", "donor", "donors", "parent", "parents", "staff", "agency", "committee"];
const SOURCE = ["attached", "the document", "our past", "this proposal"];
const TONE = ["warm", "formal", "plain", "celebratory", "urgent", "evidence-led", "evidence led"];

const STRUCTURE = ["bullets", "bullet points", "table", "paragraphs", "paragraph", "sections", "headings", "list", "numbered", "email", "memo"];

// ---- proper-noun whitelists ----------------------------------------------
const ORG_NAMES = (config.organizations || []).filter(Boolean);
const ORG_WORDS = ORG_NAMES.flatMap((n) => n.split(/[^A-Za-z]+/)).filter(Boolean);

const COMMON_CAP = [
  "The", "We", "Our", "This", "That", "It", "A", "An", "I", "You", "They",
  "He", "She", "But", "And", "For", "With", "Please", "Also", "Draft", "Write",
  "Summarise", "Summarize", "List", "Plan", "Tone", "Task", "Role", "Context",
  "Format", "AI", "No", "Yes", "My", "Your", "Their", "Its", "Of", "In", "On",
  "At", "To", "Make", "Give", "Show", "Turn", "Map", "Under", "About", "Draft",
];
const PLACE = [
  "America", "US", "USA", "United", "States",
];

const COMMON_SET = new Set(COMMON_CAP);
const PLACE_SET = new Set(PLACE);
const NAME_WHITELIST = new Set([...COMMON_CAP, ...PLACE, ...ORG_WORDS]);

// ---- fixed miss sentences -------------------------------------------------
export const MISS = {
  role_empty: "Start with who the AI should be, name a job, like 'a grants officer'.",
  role_generic: "'An assistant' could be anyone. Name the actual job you want it to do.",
  role_no_job_noun: "You have described someone but not named the job. Name the role, like 'grants officer'.",
  role_no_qualifier: "You have named a job but not how experienced or in what field. Try 'who has done this for ten years in education'.",
  task_empty: "Say the one thing you want produced, starting with a verb like 'draft' or 'summarise'.",
  task_no_verb: "Start with an action verb, draft, write, summarise, so it knows what to do.",
  task_no_object: "You have a verb but not what to act on. Name the thing to produce.",
  task_two_verbs: "There are two jobs in here. Pick one and run the other as a second prompt.",
  task_hedge: "'Help me with' leaves it guessing. Say the one thing you want produced.",
  context_no_org: "Nothing here tells the AI which organization this is. Add your name, your program, or a number about who you serve.",
  context_thin: "One fact is not enough. Add who you serve and who the output is going to.",
  format_empty: "Without this you get whatever shape it feels like. Say bullets, or paragraphs, or a table, and how long.",
  format_no_structure: "Name the shape, bullets, a table, paragraphs, so it doesn't guess.",
  format_no_bound: "You have said what shape but not how much. Add a word count or a number of items.",
};

export const SCAFFOLDS = {
  role: "You are a [job title] with [how many] years in [field].",
  task: "[Verb] the [specific thing], in [limit].",
  context: "We [do what] for [how many] [who] in [where]. This is going to [audience].",
  format: "[Bullets / paragraphs / a table], [how many], no more than [limit].",
};

export const ELEMENT_ORDER = ["role", "task", "context", "format"];

// ---- helpers --------------------------------------------------------------
const has = (t, arr) => arr.some((w) => t.includes(w));
const wordCount = (t) => (t.trim() ? t.trim().split(/\s+/).length : 0);

function detectOrgToken(raw) {
  const lc = raw.toLowerCase();
  if (ORG_NAMES.some((n) => lc.includes(n.toLowerCase()))) return true;
  // Fallback when no org names are configured: any proper noun that is not a
  // common word, or any number, counts as an organization-specific token.
  if (/\d/.test(raw)) return true;
  const caps = raw.match(/\b[A-Z][A-Za-z'’.-]{1,}\b/g) || [];
  return caps.some((w) => !COMMON_SET.has(w) && !PLACE_SET.has(w));
}

// ---- per-element scoring --------------------------------------------------
function scoreRole(raw) {
  const t = raw.toLowerCase().trim();
  const empty = t.length === 0;
  const tokensNoArticles = t.split(/\s+/).filter((w) => w && !["a", "an", "the"].includes(w));
  const genericOnly =
    tokensNoArticles.length === 1 &&
    ROLE_GENERIC.includes(tokensNoArticles[0].replace(/[^a-z]/g, ""));

  const c1 = !empty && wordCount(t) > 2;
  const c2 = has(t, ROLE_JOB_NOUNS);
  const c3 =
    /\b\d+\s*years?\b/.test(t) ||
    has(t, ROLE_QUALIFIER_PHRASES) ||
    has(t, ROLE_QUALIFIER_ADJ);

  let score = (c1 ? 1 : 0) + (c2 ? 1 : 0) + (c3 ? 1 : 0);
  if (genericOnly) score = Math.min(score, 1);

  let miss = null;
  if (empty) miss = MISS.role_empty;
  else if (genericOnly) miss = MISS.role_generic;
  else if (!c2) miss = MISS.role_no_job_noun;
  else if (!c3) miss = MISS.role_no_qualifier;

  return { score, miss };
}

function scoreTask(raw) {
  const t = raw.toLowerCase().trim();
  const empty = t.length === 0;
  const verbs = TASK_VERBS.filter((v) => new RegExp(`\\b${v}\\b`).test(t));
  const hedge = has(t, TASK_HEDGES);

  let score = empty ? 0 : 1;
  const hasVerb = verbs.length >= 1;
  if (hasVerb) score += 1;

  let hasObject = false;
  if (hasVerb) {
    const idx = t.indexOf(verbs[0]);
    const after = t.slice(idx + verbs[0].length).trim();
    const afterWords = after.split(/\s+/).filter((w) => w && !TASK_STOP.includes(w));
    hasObject = afterWords.length >= 1;
  }
  if (hasObject) score += 1;

  if (verbs.length >= 2 || hedge) score = Math.min(score, 2);

  let miss = null;
  if (empty) miss = MISS.task_empty;
  else if (verbs.length >= 2) miss = MISS.task_two_verbs;
  else if (hedge) miss = MISS.task_hedge;
  else if (!hasVerb) miss = MISS.task_no_verb;
  else if (!hasObject) miss = MISS.task_no_object;

  return { score, miss };
}

function scoreContext(raw) {
  const t = raw.toLowerCase();
  const cats = [];
  if (/\d/.test(t) && has(t, PEOPLE_NOUNS)) cats.push("serve");
  if (has(t, AUDIENCE)) cats.push("audience");
  if (
    /\b\d+\s*words?\b/.test(t) ||
    /\bmust not\b/.test(t) ||
    /\bcannot\b/.test(t) ||
    /\bmust\b/.test(t) ||
    t.includes("deadline")
  )
    cats.push("constraint");
  if (has(t, SOURCE)) cats.push("source");
  const orgToken = detectOrgToken(raw);
  if (orgToken) cats.push("org");
  if (has(t, TONE)) cats.push("tone");

  const n = cats.length;
  let score = n === 0 ? 0 : n === 1 ? 1 : n === 2 ? 2 : 3;
  if (!orgToken) score = Math.min(score, 2); // the single most important rule

  let miss = null;
  if (!orgToken) miss = MISS.context_no_org;
  else if (n < 3) miss = MISS.context_thin;

  return { score, miss };
}

function scoreFormat(raw) {
  const t = raw.toLowerCase().trim();
  const empty = t.length === 0;
  let score = empty ? 0 : 1;
  const hasStruct = has(t, STRUCTURE);
  if (hasStruct) score += 1;
  const hasBound =
    /\b\d+\s*(words?|paragraphs?|items?|questions?|sentences?|bullets?|headings?)\b/.test(t) ||
    has(t, ["under", "no more than", "at most"]);
  if (hasBound) score += 1;

  let miss = null;
  if (empty) miss = MISS.format_empty;
  else if (!hasStruct) miss = MISS.format_no_structure;
  else if (!hasBound) miss = MISS.format_no_bound;

  return { score, miss };
}

// ---- PII guard ------------------------------------------------------------
export function detectPII(fields) {
  const all = Object.values(fields).join("  ");
  const kinds = [];

  if (/[^\s@]+@[^\s@]+\.[^\s@]+/.test(all)) kinds.push("an email address");

  const phone = /(\+?\d[\d\s().-]{8,}\d)/.test(all);
  if (phone) kinds.push("a phone number");
  else if (/\d{5,}/.test(all)) kinds.push("a long number");

  const re = /\b([A-Z][a-z'’-]+)\s+([A-Z][a-z'’-]+)\b/g;
  let m;
  while ((m = re.exec(all))) {
    const [, w1, w2] = m;
    if (NAME_WHITELIST.has(w1) || NAME_WHITELIST.has(w2)) continue;
    const pair = `${w1} ${w2}`.toLowerCase();
    if (ORG_NAMES.some((n) => n.toLowerCase().includes(pair))) continue;
    kinds.push("what looks like a personal name");
    break;
  }

  const uniq = [...new Set(kinds)];
  return { blocked: uniq.length > 0, kinds: uniq };
}

// ---- main entry -----------------------------------------------------------
export function scorePrompt(fields) {
  const parts = {
    role: scoreRole(fields.role || ""),
    task: scoreTask(fields.task || ""),
    context: scoreContext(fields.context || ""),
    format: scoreFormat(fields.format || ""),
  };

  const scores = {
    role: parts.role.score,
    task: parts.task.score,
    context: parts.context.score,
    format: parts.format.score,
  };
  const misses = {
    role: parts.role.miss,
    task: parts.task.miss,
    context: parts.context.miss,
    format: parts.format.miss,
  };
  const total = scores.role + scores.task + scores.context + scores.format;

  // Weakest: lowest score, ties broken preferring context (the taught lesson).
  const tieOrder = ["context", "role", "task", "format"];
  let weakest = tieOrder[0];
  for (const k of tieOrder) if (scores[k] < scores[weakest]) weakest = k;

  return {
    scores,
    misses,
    total,
    pass: total >= 9,
    weakest,
    pii: detectPII(fields),
  };
}
